<template>
  <div
    class="rounded shadow-sm bg-white px-3 py-2 my-5 md:my-0 md:px-8 md:py-5 max-w-screen-lg mx-auto"
  >
    <div class="flex flex-col py-3">
      <div class="flex flex-col md:px-20">
        <h2 class="text-2xl text-brand-blue">Almost Done, Personalize Your Form</h2>
      </div>
    </div>

    <div class="flex flex-col md:px-20">
      <div class="flex flex-col py-3 mb-5">
        <h5 class="block text-2xl mb-3">Name Your Form</h5>
        <div class="flex items-center py-1">
          <EyeOffIcon class="w-5 h-5 text-brand-dark-gray mr-2" />
          <p class="text-brand-dark-gray text-xs py-1">
            Form name is only visible to you. This helps you to organize your application forms.
          </p>
        </div>
        <SingleLineInput
          :value="name"
          @input="name = $event.target.value"
          placeholder="Ex: Maple Realty Tenant Screening Application"
        ></SingleLineInput>
      </div>

      <div class="flex flex-col py-3">
        <h5 class="block text-2xl mb-3">Customize Application Form Link</h5>
        <p class="text-brand-dark-gray text-xs py-1">
          This will link your applicants directly to your application
        </p>
        <div class="flex flex-row items-center py-2">
          <div class="border border-brand-mid-gray rounded-tl rounded-bl py-2 pl-5 pr-3 h-full">
            <p class="">{{ origin }}/</p>
          </div>
          <div class="w-full">
            <input
              type="text"
              class="w-full px-4 rounded-tr rounded-br placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green"
              placeholder="slug"
              :class="[
                !!slug ? 'bg-brand-light-gray' : '',
                highlight
                  ? 'border-2 border-brand-green'
                  : 'border border-brand-mid-gray border-l-0'
              ]"
              :style="{
                'box-shadow': highlight ? 'box-shadow: 0 0 2px 1px #3ec19d !important' : ''
              }"
              v-model="slug"
            />
          </div>
        </div>
        <div class="flex flex-col">
          <div class="flex items-center py-1">
            <CheckCircleIcon
              :class="['w-6 h-6  mr-3', isComboValid ? 'text-brand-green' : 'text-brand-dark-gray']"
            />
            <p class="text-brand-dark-gray italic text-xs flex-1">
              Can contain a combination of lowercase letters, numbers, underscores and hyphens. No
              spaces.
            </p>
          </div>
          <div class="flex items-center py-1">
            <CheckCircleIcon
              :class="['w-6 h-6 mr-3', isLengthValid ? 'text-brand-green' : 'text-brand-dark-gray']"
            />
            <p class="text-brand-dark-gray italic text-xs flex-1">
              Must be at least 6 characters long.
            </p>
          </div>
        </div>
        <p class="text-brand-red text-sm" v-if="slugErrorMessage">
          {{ slugErrorMessage }}
        </p>
      </div>
    </div>

    <div class="flex flex-row py-3 md:px-20">
      <div class="flex-1 flex items-center w-1/2 pr-10">
        <div class="border border-brand-mid-gray w-full"></div>
      </div>
      <div class="flex items-center">
        <h2 class="font-light">or</h2>
      </div>
      <div class="flex-1 flex items-center w-1/2 pl-10">
        <div class="flex border border-brand-mid-gray w-full"></div>
      </div>
    </div>
    <div class="flex flex-row justify-center py-3 md:px-20">
      <h5 class="text-center">
        Let Us
        <span class="text-brand-green cursor-pointer hover:underline" @click="createSlug"
          >Create a URL for You</span
        >
      </h5>
    </div>

    <div class="flex flex-col justify-between py-5">
      <div class="w-full md:w-1/3 mx-auto py-1">
        <PrimaryButton
          @click="createForm"
          :disabled="isApplicationTemplateLoading || !isSlugValid"
          :is-loading="isApplicationTemplateLoading"
          >Finish</PrimaryButton
        >

        <p @click="backToReports" class="text-brand-green text-center my-8 cursor-pointer">Back</p>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex"
import randomize from "randomatic"
import { CheckCircleIcon, EyeOffIcon } from "@heroicons/vue/outline"

import {
  APPLICATION_TEMPLATE_CREATE,
  APPLICATION_TEMPLATE_CHECK_AVAILABLE,
  APPLICATION_TEMPLATE_GET_LIST
} from "@/store/types"

export default {
  components: {
    CheckCircleIcon,
    EyeOffIcon
  },
  computed: {
    ...mapGetters([
      "getCurrentUser",
      "getCurrentNewApplicationTemplate",
      "isApplicationTemplateLoading"
    ]),
    origin() {
      return location.origin
    },
    isSlugValid() {
      return this.isComboValid && this.isLengthValid
    },
    isComboValid() {
      const result = /[^a-z0-9_-]+/.test(this.slug)
      return !result
    },
    isLengthValid() {
      return this.slug.length >= 6
    }
  },
  async mounted() {
    if (!this.getCurrentNewApplicationTemplate) {
      this.$router.push({ name: "dashboard_new_form_reports" })
    }

    this.slug = this.getCurrentUser.username.split("@")[0].replace(/[^A-Z0-9]+/gi, "")

    try {
      const resp = await this.$store.dispatch(APPLICATION_TEMPLATE_CHECK_AVAILABLE, {
        slug: this.slug
      })
      if (!resp.data.available) {
        this.createSlug()
      }
    } catch (err) {
      console.log(err)
    }
  },
  data() {
    return {
      name: "",
      slug: "",
      slugErrorMessage: "",
      highlight: false
    }
  },
  methods: {
    backToReports() {
      this.$router.push({ name: "dashboard_new_form_reports" })
    },
    createSlug() {
      this.slug = randomize("a0", 6)

      this.highlight = true
    },
    async createForm() {
      const { slug, name } = this
      const { selected, fee } = this.getCurrentNewApplicationTemplate

      try {
        await this.$store.dispatch(APPLICATION_TEMPLATE_CREATE, {
          slug,
          name,
          selected,
          fee
        })
        await this.$store.dispatch(APPLICATION_TEMPLATE_GET_LIST, { page: 1 })

        this.$router.push({ name: "dashboard_forms" })
      } catch (err) {
        console.log(err)
      }
    }
  }
}
</script>
